/**
 * Blocker Service
 * AIDLC Administrative Backend
 *
 * API calls for blocker CRUD and cross-project dependency tracking.
 */

import apiClient from './api';
import type { Blocker } from '../types/projects';

const BLOCKERS_PATH = '/blockers';

/**
 * Fetch blockers for a project
 */
export async function fetchBlockers(projectId: string): Promise<Blocker[]> {
  const response = await apiClient.get<Blocker[]>(`${BLOCKERS_PATH}?projectId=${encodeURIComponent(projectId)}`);
  return response.data;
}

/**
 * Fetch all cross-project blockers
 */
export async function fetchCrossProjectBlockers(): Promise<Blocker[]> {
  const response = await apiClient.get<Blocker[]>(`${BLOCKERS_PATH}/cross-project`);
  return response.data;
}

/**
 * Create a new blocker
 */
export async function createBlocker(data: {
  projectId: string;
  backlogItemId?: string | null;
  sprintId?: string | null;
  blockedAgentId: string;
  resolverAgentId?: string | null;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  crossProjectDependency?: {
    dependsOnProjectId: string;
    dependsOnItemId?: string | null;
    description: string;
  } | null;
}): Promise<Blocker> {
  const response = await apiClient.post<Blocker>(BLOCKERS_PATH, data);
  return response.data;
}

/**
 * Update blocker details
 */
export async function updateBlocker(id: string, data: Partial<Blocker>): Promise<Blocker> {
  const response = await apiClient.put<Blocker>(`${BLOCKERS_PATH}/${id}`, data);
  return response.data;
}

/**
 * Resolve a blocker
 */
export async function resolveBlocker(id: string, resolvedBy: string): Promise<Blocker> {
  const response = await apiClient.put<Blocker>(`${BLOCKERS_PATH}/${id}/resolve`, { resolvedBy });
  return response.data;
}
